export const MAIL_FOLDERS = [
    { key: "junk", label: "Junk / Spam", icon: "bi-shield-exclamation" },
    { key: "inbox", label: "Inbox", icon: "bi-inbox" },
    { key: "sent", label: "Sent", icon: "bi-send" },
    { key: "drafts", label: "Drafts", icon: "bi-file-earmark" },
    { key: "trash", label: "Trash", icon: "bi-trash" },
];

export function getInitials(email) {
    const name = email.split("@")[0];
    return name.substring(0, 2).toUpperCase();
}

export function formatDisplayDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function extractSenderName(from) {
    if (!from) return "Unknown";
    const match = from.match(/^(.+?)\s*</);
    return match ? match[1].replace(/"/g, "").trim() : from.split("@")[0];
}

/** Display name for a CRM mailbox (not country label) */
export function formatMailboxLabel(email) {
    if (!email) return "Mailbox";
    const local = (email.split("@")[0] || email).trim();
    return local
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatMailboxDomain(email) {
    if (!email || !email.includes("@")) return "";
    return email.split("@")[1].toLowerCase();
}

export function getStoredPassword(email) {
    try {
        return sessionStorage.getItem(`mail_pass_${email}`) || "";
    } catch {
        return "";
    }
}

export function storePassword(email, pass) {
    try {
        sessionStorage.setItem(`mail_pass_${email}`, pass);
    } catch {
        /* ignore */
    }
}

export function clearStoredPassword(email) {
    try {
        sessionStorage.removeItem(`mail_pass_${email}`);
    } catch {
        /* ignore */
    }
}

export function getMailSignature(email) {
    try {
        const perAccount = localStorage.getItem(`crm_mail_sig_${email}`);
        if (perAccount) return perAccount;
        return localStorage.getItem("crm_mail_sig_default") || "";
    } catch {
        return "";
    }
}

export function saveMailSignature(email, text) {
    try {
        if (email) {
            localStorage.setItem(`crm_mail_sig_${email}`, text);
        } else {
            localStorage.setItem("crm_mail_sig_default", text);
        }
    } catch {
        /* ignore */
    }
}

const SIG_HTML_PREFIX = "crm_mail_sig_html_";

export function getMailSignatureHtml(email) {
    try {
        const key = email ? `${SIG_HTML_PREFIX}${email}` : `${SIG_HTML_PREFIX}default`;
        const html = localStorage.getItem(key);
        if (html) return html;
        const text = getMailSignature(email);
        if (!text) return "";
        return text.replace(/\n/g, "<br>");
    } catch {
        return "";
    }
}

export function saveMailSignatureHtml(email, html) {
    const key = email ? `${SIG_HTML_PREFIX}${email}` : `${SIG_HTML_PREFIX}default`;
    localStorage.setItem(key, html || "");
    saveMailSignature(email, stripHtmlForQuote(html || ""));
}

export function isHtmlContent(value) {
    return /<[a-z][\s\S]*>/i.test(String(value || ""));
}

export function decodeHtmlEntities(text, maxPasses = 3) {
    const NAMED = {
        nbsp: "\u00a0",
        amp: "&",
        lt: "<",
        gt: ">",
        quot: '"',
        apos: "'",
        rsquo: "\u2019",
        lsquo: "\u2018",
        rdquo: "\u201d",
        ldquo: "\u201c",
        mdash: "\u2014",
        ndash: "\u2013",
        hellip: "\u2026",
        bull: "\u2022",
    };
    const once = (value) =>
        String(value || "")
            .replace(/&#x([0-9a-f]+);/gi, (_, hex) => {
                const cp = parseInt(hex, 16);
                return Number.isFinite(cp) ? String.fromCodePoint(cp) : _;
            })
            .replace(/&#(\d+);/g, (_, dec) => {
                const cp = parseInt(dec, 10);
                return Number.isFinite(cp) ? String.fromCodePoint(cp) : _;
            })
            .replace(/&([a-z]+);/gi, (full, name) => NAMED[name.toLowerCase()] || full);

    let out = String(text || "");
    for (let i = 0; i < maxPasses; i++) {
        const next = once(out);
        if (next === out) break;
        out = next;
    }
    return out;
}

/** Fix UTF-8 text that was wrongly read as Latin-1 (e.g. â€™ → ') */
export function fixEmailDisplayText(text) {
    if (!text) return "";
    let out = String(text);
    if (/(?:Ã.|â€.|ðŸ|ï¿½|Â[\s\u00a0])/.test(out)) {
        try {
            const fixed = new TextDecoder("utf-8", { fatal: false }).decode(
                Uint8Array.from([...out].map((c) => c.charCodeAt(0) & 0xff))
            );
            if (fixed && fixed !== out) out = fixed;
        } catch {
            /* ignore */
        }
    }
    if (/&(?:#x?[0-9a-f]+|[a-z]+);/i.test(out)) {
        out = decodeHtmlEntities(out);
    }
    return out;
}

export function escapeHtml(text) {
    return String(text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

export function getComposeSignatureHtml(mailboxEmail) {
    return getMailSignatureHtml(mailboxEmail).trim();
}

export function formatQuotedMessageHtml(message) {
    if (!message) return "";
    const inner = message.isHtml
        ? message.body
        : escapeHtml(String(message.body || "")).replace(/\n/g, "<br>");
    return (
        `<br><br><hr style="border:none;border-top:1px solid #ccc;margin:16px 0">` +
        `<p style="color:#605e5c;font-size:12px;margin:0 0 8px">On ${escapeHtml(message.date || "")}, ${escapeHtml(message.from || "sender")} wrote:</p>` +
        `<blockquote style="margin:0;padding:8px 12px;border-left:3px solid #0078d4;color:#323130">${inner}</blockquote>`
    );
}

/** Rich compose: draft HTML + signature + quoted thread */
export function buildReplyComposeHtml(message, mailboxEmail, userDraftHtml = "") {
    const sigHtml = getComposeSignatureHtml(mailboxEmail);
    let draft = String(userDraftHtml || "").trim();
    if (sigHtml && !draft.includes(sigHtml.slice(0, 40))) {
        draft = draft ? `${draft}<br><br>${sigHtml}` : sigHtml;
    }
    const quote = formatQuotedMessageHtml(message);
    return draft ? `${draft}${quote}` : quote;
}

export function appendSignatureHtmlToDraft(draftHtml, mailboxEmail) {
    const sigHtml = getComposeSignatureHtml(mailboxEmail);
    if (!sigHtml) return draftHtml || "";
    const draft = String(draftHtml || "").trim();
    if (draft.includes(sigHtml.slice(0, 40))) return draft;
    return draft ? `${draft}<br><br>${sigHtml}` : sigHtml;
}

/** Parse comma-separated / RFC822 address headers into lowercase emails */
export function parseAddressList(value) {
    if (!value) return [];
    const raw = String(value);
    const emails = [];
    const angleMatches = raw.match(/<([^>]+@[^>]+)>/g);
    if (angleMatches?.length) {
        angleMatches.forEach((m) => emails.push(m.slice(1, -1).trim().toLowerCase()));
    } else {
        raw.split(",").forEach((part) => {
            const trimmed = part.trim();
            if (!trimmed) return;
            const match = trimmed.match(/<([^>]+)>/);
            const addr = (match ? match[1] : trimmed).trim().toLowerCase();
            if (addr.includes("@")) emails.push(addr);
        });
    }
    return [...new Set(emails.filter(Boolean))];
}

/** Reply-all: preserve original To vs Cc placement (minus self) */
export function buildReplyAllRecipients(message, myEmail) {
    const me = String(myEmail || "").trim().toLowerCase();
    const fromRaw = message?.from || "";
    const senderMatch = fromRaw.match(/<([^>]+)>/);
    const senderRaw = senderMatch ? senderMatch[1].trim() : fromRaw.trim();
    const sender = senderRaw.toLowerCase();
    const toAddrs = parseAddressList(message?.to);
    const ccAddrs = parseAddressList(message?.cc);

    const replyToList = [];
    if (sender && sender !== me) replyToList.push(senderRaw);

    toAddrs.forEach((addr) => {
        if (addr !== me && addr !== sender && !replyToList.some((x) => x.toLowerCase() === addr)) {
            replyToList.push(addr);
        }
    });

    const replyCcList = ccAddrs.filter(
        (addr) =>
            addr !== me &&
            addr !== sender &&
            !replyToList.some((x) => x.toLowerCase() === addr)
    );

    return {
        replyTo: replyToList.join(", "),
        replyCc: replyCcList.join(", "),
    };
}

export function stripHtmlForQuote(html) {
    if (!html) return "";
    return html
        .replace(/<style[\s\S]*?<\/style>/gi, " ")
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export function formatQuotedMessage(message) {
    if (!message) return "";
    const text = message.isHtml
        ? stripHtmlForQuote(message.body)
        : String(message.body || "").trim();
    return `\n\n---\nOn ${message.date || ""}, ${message.from || "sender"} wrote:\n${text}`;
}

/** Compose body: optional draft + signature + quoted prior message (plain text fallback) */
export function buildReplyComposeBody(message, mailboxEmail, userDraft = "") {
    const sig = getMailSignature(mailboxEmail).trim();
    let body = String(userDraft || "").trimEnd();
    if (sig && !body.includes(sig)) {
        body = body ? `${body}\n\n${sig}` : sig;
    }
    const quote = formatQuotedMessage(message);
    return body ? `${body}${quote}` : quote.trimStart();
}

export function appendSignatureToBody(body, mailboxEmail) {
    const sigHtml = getComposeSignatureHtml(mailboxEmail);
    if (sigHtml) return appendSignatureHtmlToDraft(body, mailboxEmail);
    const sig = getMailSignature(mailboxEmail).trim();
    if (!sig) return body || "";
    const trimmed = String(body || "").trimEnd();
    if (trimmed.includes(sig)) return trimmed;
    return trimmed ? `${trimmed}\n\n${sig}` : sig;
}

export function plainTextFromEmailBody(body) {
    return stripHtmlForQuote(String(body || "")).replace(/\s+/g, " ").trim();
}

export const TRANSLATE_LANGUAGES = [
    { code: "en", label: "English" },
    { code: "ur", label: "Urdu" },
    { code: "ar", label: "Arabic" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "zh", label: "Chinese" },
    { code: "hi", label: "Hindi" },
    { code: "pt", label: "Portuguese" },
    { code: "ru", label: "Russian" },
    { code: "ja", label: "Japanese" },
    { code: "ko", label: "Korean" },
    { code: "it", label: "Italian" },
    { code: "tr", label: "Turkish" },
    { code: "nl", label: "Dutch" },
];

export function formatAttachmentSize(bytes) {
    if (!bytes) return "0 KB";
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function totalAttachmentSize(files) {
    return (files || []).reduce((sum, f) => sum + (f.size || 0), 0);
}

export function getAttachmentIcon(contentType, filename) {
    const type = (contentType || "").toLowerCase();
    const name = (filename || "").toLowerCase();
    if (type.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) {
        return "bi-file-earmark-image";
    }
    if (type === "application/pdf" || name.endsWith(".pdf")) return "bi-file-earmark-pdf";
    if (type.includes("word") || /\.(doc|docx)$/i.test(name)) return "bi-file-earmark-word";
    if (type.includes("sheet") || type.includes("excel") || /\.(xls|xlsx|csv)$/i.test(name)) {
        return "bi-file-earmark-spreadsheet";
    }
    if (type.startsWith("video/") || /\.(mp4|mov|avi|webm)$/i.test(name)) return "bi-file-earmark-play";
    return "bi-file-earmark";
}
